import React from "react";
import { SmilePlus } from "lucide-react";
import { DM_QUICK_REACT_EMOJIS } from "@/lib/dmApi";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function reactionHoverLabel(r) {
  const names = Array.isArray(r?.users) ? r.users.filter(Boolean) : [];
  if (names.length === 0) {
    return r?.reacted ? "Remove reaction" : "Add reaction";
  }
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * Slack-like reaction chips + quick picker (sits below the message bubble).
 */
export default function DmMessageReactions({
  reactions = [],
  onReact,
  inverse = false,
  compact = false,
  align = "start",
}) {
  const list = reactions || [];
  if (list.length === 0) return null;

  const handle = async (emoji) => {
    try {
      await onReact?.(emoji);
    } catch (err) {
      console.error("Failed to react:", err);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "flex flex-wrap items-center gap-0.5 mt-0.5",
          align === "end" || inverse ? "justify-end" : "justify-start"
        )}
      >
        {list.map((r) => (
          <Tooltip key={r.emoji}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => handle(r.emoji)}
                className={cn(
                  "inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0 text-[11px] leading-none h-5 transition-colors",
                  r.reacted
                    ? inverse
                      ? "border-primary-foreground/60 bg-black/20"
                      : "border-primary/40 bg-primary/10"
                    : inverse
                      ? "border-primary-foreground/30 bg-black/10 hover:bg-black/20"
                      : "border-border bg-background/80 hover:bg-muted"
                )}
              >
                <span className="text-sm leading-none">{r.emoji}</span>
                <span className="tabular-nums opacity-80 text-[10px]">
                  {r.count}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {reactionHoverLabel(r)}
            </TooltipContent>
          </Tooltip>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-5 w-5 rounded-full opacity-70 hover:opacity-100",
                compact && "h-5 w-5"
              )}
              title="Add reaction"
              aria-label="Add reaction"
            >
              <SmilePlus className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="flex w-auto min-w-0 flex-row gap-0.5 p-1.5"
          >
            {DM_QUICK_REACT_EMOJIS.map((emoji) => (
              <DropdownMenuItem
                key={emoji}
                className="h-8 w-8 cursor-pointer justify-center p-0 text-base"
                onSelect={() => {
                  void handle(emoji);
                }}
              >
                {emoji}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
}
